import { css } from 'lit';
import componentStyles from '../styles/component.styles';
export default css `
  ${componentStyles}
  /**
--------------------------------------------------
* General
--------------------------------------------------
*/
:host {
    display: block;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  :host([edit-mode]) {
    position: relative;
  }

  .edit {
    pointer-events: none;
  }

  /**
--------------------------------------------------
* General
--------------------------------------------------
*/
  header {
    border-bottom: var(--p-header-border-width) solid var(--p-header-border-color);
    background-color: var(--p-header-bg);
    padding-inline: var(--p-gap);
    position: relative;
  }

  .search {
    grid-area: search;
  }

  form {
    width: 100%;
  }

  a,
  button {
    font-family: var(--p-header-font, var(--p-font));
  }

  /**
--------------------------------------------------
* Skip link
--------------------------------------------------
*/
  .skip-to-content {
    position: absolute;
    left: -9999px;
    z-index: 999;
    padding: 1em;
    background-color: black;
    color: white;
    opacity: 0;
  }
  .skip-to-content:focus {
    left: 0;
    opacity: 1;
  }

  /**
--------------------------------------------------
* Wrapper
--------------------------------------------------
*/
  header.contained {
    padding-inline: 0;
  }

  .wrapper {
    display: grid;
    column-gap: var(--p-gap);
    width: min(var(--p-outer-wrap-width), 100%);
    margin-inline: auto;
  }

  .contained .wrapper {
    width: min(var(--p-container-width), 100% - 2 * var(--p-gap));
    margin-inline: auto;
  }

  /**
--------------------------------------------------
* Default Layout
--------------------------------------------------
*/
  header.default-layout .wrapper {
    grid-template-areas:
      'logo nav options'
      'search search search';
    grid-template-rows: var(--p-header-height) auto;
  }
  header.default-layout.nav-left .wrapper {
    grid-template-columns: auto auto 1fr;
  }
  header.default-layout.nav-center .wrapper {
    grid-template-columns: 1fr auto 1fr;
  }
  header.default-layout.nav-right .wrapper {
    grid-template-columns: 1fr auto auto;
  }

  /* Search */
  header.default-layout .search {
    border-top: 1px solid var(--p-header-border-color);
    --p-input-margin-bottom: 0;
    height: 60px;
    align-items: center;
    z-index: 1;
    display: none;
  }

  header.default-layout .search.show {
    display: flex;
  }

  header.default-layout .search-wrap {
    width: min(var(--p-search-container-width, 480px), 100% - 2 * var(--p-gap));
    margin-inline: auto;
  }

  @media (max-width: 768px) {
    header.default-layout {
      --p-header-height: 50px;
    }
  }

  /**
--------------------------------------------------
* Search Layout
--------------------------------------------------
*/
  header.search-layout .wrapper {
    height: var(--p-header-height);
    grid-template-columns: 1fr minmax(0, 480px) 1fr;
    grid-template-rows: 65% 35%;
    grid-template-areas:
      'logo search options'
      'nav nav nav';
  }
  header.search-layout .nav {
    border-top: 1px solid var(--p-header-border-color);
  }

  /* Search */
  header.search-layout .search-wrap {
    display: flex;
    align-items: center;
    height: 100%;
  }
  header.search-layout #search-input {
    width: 100%;
    --p-input-margin-bottom: 0;
  }

  @media (max-width: 768px) {
    header.search-layout {
      --p-header-height: 100px;
    }
    header.search-layout .wrapper {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 50px 50px;
      grid-template-areas:
        'logo options'
        'search search';
    }

    header.search-layout .logo {
      height: 50px;
    }
  }

  /**
--------------------------------------------------
* Logo
--------------------------------------------------
*/
  .logo {
    grid-area: logo;
    height: var(--p-header-height);
    display: flex;
    align-items: center;
  }

  .search-layout .logo {
    height: calc(var(--p-header-height) * 0.65);
  }

  .logo a {
    height: var(--p-logo-height);
    display: flex;
    align-items: center;
  }

  .logo img {
    display: block;
    height: 100%;
    max-width: 100%;
    object-fit: contain;
    object-position: left center;
  }

  /**
--------------------------------------------------
* Main Nav
--------------------------------------------------
*/
  .nav {
    grid-area: nav;
    display: flex;
    position: relative;
    z-index: 2;
  }

  .nav-center .nav {
    justify-content: center;
  }

  .nav-right .nav {
    justify-content: flex-end;
  }

  .nav ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .nav > ul {
    display: flex;
    align-items: center;
    height: 100%;
    gap: var(--p-main-nav-gap, 30px);
  }

  .nav > ul > li {
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
  }

  .nav li {
    position: relative;
  }

  .nav a,
  .nav .linkless-expand {
    color: var(--p-header-text-color);
    font-weight: var(--p-main-nav-font-weight, var(--p-font-weight));
    font-size: var(--p-main-nav-font-size, 1rem);
    font-style: var(--p-main-nav-font-style, normal);
    text-decoration: none;
    display: flex;
    align-items: center;
    height: 100%;
    line-height: 1.3;
  }

  .nav a:hover,
  .nav .linkless-expand:hover {
    color: var(--p-header-text-color-hover, var(--p-header-text-color));
    text-decoration: var(--p-main-nav-hover-decoration, underline);
    text-decoration-color: var(--p-main-nav-hover-decoration-color, var(--p-header-contrast-400));
    text-decoration-thickness: 2px;
    text-underline-offset: 0.2em;
  }

  .nav li.selected > a,
  .nav li.selected.linkless .linkless-expand {
    color: var(--p-main-nav-selected-color, inherit);
    text-decoration: var(--p-main-nav-selected-decoration, underline);
    text-decoration-color: var(--p-main-nav-selected-decoration-color, var(--p-brand));
    text-decoration-thickness: 2px;
    text-underline-offset: 0.2em;
  }

  .nav ul ul a {
    font-size: var(--p-main-nav-dropdown-font-size, var(--p-main-nav-font-size, 1rem));
    padding: var(--p-main-nav-dropdown-item-padding-over-under, 8px)
      var(--p-main-nav-dropdown-item-padding-inline, 16px);
  }

  .nav ul ul {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    transition: all 0.2s linear;
    background: var(--p-header-bg, var(--p-bg));
    border: 1px solid var(--p-header-border-color, var(--p-border));
    border-top: 0;
    box-shadow: var(--p-header-dropdown-shadow, var(--p-shadow));
    width: var(--p-header-dropdown-width, 200px);
    top: 100%;
    left: calc(var(--p-header-dropdown-item-padding-inline, 16px) * -1);
    /* right: 0; */
    padding: var(--p-header-dropdown-item-padding-over-under, 5px) 0;
    border-radius: 0 0 4px 4px;
    max-height: calc(100vh - var(--p-header-height));
    overflow-y: auto;
  }

  li.has-sub a,
  button.linkless-expand {
    position: relative;
    padding-right: 29px;
    padding-left: 0;
  }

  .expand-sub,
  .linkless-expand .caret-container {
    background-color: var(--p-header-contrast-100);
    border: 0;
    padding: 0;
    cursor: pointer;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--p-header-text-color);
  }

  .expand-sub:hover,
  .linkless-expand:hover .caret-container {
    background-color: var(--p-header-contrast-200);
  }

  .expand-sub svg,
  .linkless-expand .caret-container {
    pointer-events: none;
  }

  .linkless-expand[aria-expanded='true'] .caret-container,
  .expand-sub[aria-expanded='true'] {
    background-color: var(--p-header-contrast-400);
    /* color: var(--p-button-primary-text-color); */
  }

  .expand-sub[aria-expanded='true'] + ul,
  .linkless-expand[aria-expanded='true'] + ul,
  .nav ul li:hover ul {
    visibility: visible;
    opacity: 1;
  }

  @media (min-width: 768px) {
    .nav > ul > li.has-sub ul ul,
    .nav > ul > li.has-sub ul .expand-sub {
      display: none; // hide on desktop
    }
  }
  @media (max-width: 768px) {
    .nav {
      position: fixed;
      top: var(--p-header-height);
      right: 0;
      left: 0;
      height: calc(100svh - var(--p-header-height));
      background: var(--p-header-bg);
      border-top: 1px solid var(--p-header-border-color);
      display: block;
      overflow-y: auto;
      visibility: hidden;
      opacity: 0;
    }

    .nav.show {
      opacity: 1;
      visibility: visible;
    }

    .nav > ul {
      display: block;
      height: auto;
    }

    .nav li.has-sub {
      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-rows: auto auto;
      grid-template-areas:
        'link toggle'
        'sub sub';
    }

    .nav li.has-sub > a {
      grid-area: link;
    }

    .nav li.has-sub ul {
      padding-left: 1rem;
    }

    .nav a {
      flex-grow: 1;
      padding: 1rem var(--p-gap) !important;
      border-bottom: 1px solid var(--p-header-border-color);
    }

    .expand-sub,
    .linkless-expand .caret-container {
      width: 50px;
      height: 100%;
      border-radius: 0;
      transform: none;
      position: static;
      border-left: 1px solid var(--p-header-border-color);
      border-bottom: 1px solid var(--p-header-border-color);
      grid-area: toggle;
    }

    .nav button.linkless-expand {
      flex-grow: 1;
      border-radius: 0;
      border-bottom: 1px solid var(--p-header-border-color);
      height: 100%;
      padding: 1.2rem 14px 1.2rem var(--p-gap);
      justify-content: space-between;
      margin-left: 0; /* overwrite desktop style */
    }

    .nav button.linkless-expand .caret-container {
      position: absolute;
      right: 0;
      top: 0;
      border-bottom: 0;
    }

    .nav ul ul {
      position: static;
      display: none;
      border: 0;
      box-shadow: none;
      padding: 0;
      --p-header-dropdown-width: 100%;
      grid-area: sub;
    }

    .expand-sub[aria-expanded='true'] + ul,
    .linkless-expand[aria-expanded='true'] + ul {
      display: block;
    }
  }

  /**
--------------------------------------------------
* Options Nav
--------------------------------------------------
*/
  .options {
    grid-area: options;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    z-index: 2;
  }

  .options ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .options > ul {
    display: flex;
    height: 100%;
    gap: var(--p-option-nav-gap, 5px);
  }

  .options li {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .linkless-expand,
  .options button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--p-header-text-color);
  }

  .options button {
    font-weight: var(--p-option-nav-font-weight, var(--p-font-weight));
    font-size: var(--p-option-nav-font-size, 1rem);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    min-width: 40px;
    padding: 0 0.5em;
    border-radius: var(--p-button-border-radius);
    position: relative;
  }

  .options li:hover button,
  .options button[aria-expanded='true'] {
    background-color: var(--p-header-contrast-100);
  }

  .options button[aria-expanded='true']:hover {
    background-color: var(--p-header-contrast-200);
  }

  .options button svg {
    pointer-events: none;
  }

  .collection-count {
    position: absolute;
    top: 0;
    right: 0;
    background: var(--p-brand);
    color: var(--p-brand-contrast);
    font-size: 0.6875rem;
    border-radius: 9999px;
    height: 18px;
    min-width: 18px;
    line-height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 0.45em;
  }

  .options button.lang {
    gap: 3px;
    font-size: 0.875rem;
    line-height: 1;
  }

  .options button span {
    pointer-events: none;
  }

  .options ul ul {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    transition: all 0.2s linear;
    background: var(--p-header-bg, var(--p-bg));
    border: 1px solid var(--p-header-border-color, var(--p-border));
    border-top: 0;
    box-shadow: var(--p-header-dropdown-shadow, var(--p-shadow));
    width: var(--p-header-dropdown-width, 200px);
    top: 100%;
    right: 0;
    padding: var(--p-header-dropdown-item-padding-over-under, 5px) 0;
    border-radius: 0 0 4px 4px;
  }

  @media (max-width: 768px) {
    .options ul ul {
      width: auto;
    }
  }

  .options ul ul li {
    display: block;
  }

  .options ul ul a {
    display: block;
    color: var(--p-header-text-color);
    font-weight: var(--p-main-nav-font-weight, var(--p-font-weight));
    font-size: var(--p-main-nav-dropdown-font-size, var(--p-main-nav-font-size, 1rem));
    text-decoration: none;
    padding: var(--p-main-nav-dropdown-item-padding-over-under, 5px)
      var(--p-main-nav-dropdown-item-padding-inline, 16px);
    white-space: nowrap;
  }

  .options ul ul a:hover {
    color: var(--p-header-text-color-hover, var(--p-header-text-color));
    text-decoration: var(--p-main-nav-hover-decoration, underline);
    text-decoration-color: var(--p-header-contrast-400);
    text-decoration-thickness: 2px;
    text-underline-offset: 0.2em;
  }

  .options ul ul li.selected > a {
    text-decoration: var(--p-main-nav-selected-decoration, underline);
    text-decoration-color: var(--p-main-nav-selected-decoration-color, var(--p-brand));
    text-decoration-thickness: 2px;
    text-underline-offset: 0.2em;
  }

  .options button[aria-expanded='true'] + ul {
    visibility: visible;
    opacity: 1;
  }

  button[aria-expanded='true'] .caret {
    transform: rotate(180deg);
  }

  .toggle-mobile-nav {
    position: relative;
    width: 40px;
    height: 40px;
    background-color: red;
    display: block;
  }

  .toggle-mobile-nav::before,
  .toggle-mobile-nav::after {
    content: '';
    position: absolute;
    width: 50%;
    height: 1px;
    background-color: var(--p-header-text-color);
    transition: all 0.15s linear;
    transform-origin: 50% 50%;
  }

  .toggle-mobile-nav::before {
    top: 38%;
  }

  .toggle-mobile-nav::after {
    bottom: 38%;
  }

  .toggle-mobile-nav[aria-expanded='true']::before {
    transform: rotate(45deg) translateY(-1px);
    top: 50%;
  }

  .toggle-mobile-nav[aria-expanded='true']::after {
    transform: rotate(-45deg) translateY(1px);
    bottom: 50%;
  }

  .options li.mobile-nav-li {
    display: none;
  }

  @media (max-width: 768px) {
    .options li.mobile-nav-li {
      display: flex;
    }
    .options button span {
      display: none;
    }
  }
`;
